import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trendsAPI } from '../services/apiEndpoints';
import { TREND_LOCATIONS, DEFAULT_LOCATION } from '../data/trendLocations';
import QuickPost from './QuickPost';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import {
    MagnifyingGlassIcon,
    TargetIcon,
    CheckCircledIcon,
    PlusIcon,
    RocketIcon,
    LightningBoltIcon,
    BarChartIcon,
    ReloadIcon,
    ClockIcon,
    ArrowUpIcon,
    ExclamationTriangleIcon,
    InfoCircledIcon
} from '@radix-ui/react-icons';

function CompetitorAnalysis({ user }) {
    const navigate = useNavigate();
    const [niche, setNiche] = useState('');
    const [competitorSite, setCompetitorSite] = useState('');
    const [locationCode, setLocationCode] = useState(DEFAULT_LOCATION);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [recentSearches, setRecentSearches] = useState([]);
    const [kdFilter, setKdFilter] = useState('all');

    // QuickPost modal state
    const [showQuickPost, setShowQuickPost] = useState(false);
    const [prefilledTopic, setPrefilledTopic] = useState('');

    useEffect(() => {
        fetchRecentSearches();
    }, []);

    const fetchRecentSearches = async () => {
        try {
            const response = await trendsAPI.getContentGapRecentSearches();
            if (response.data.success) {
                setRecentSearches(response.data.data || []);
            }
        } catch {
            // Non-critical
        }
    };

    const handleAnalyze = async (forceRefresh = false, overrideNiche = undefined, overrideCompetitor = undefined, overrideLocation = undefined, fromRecentSearch = false) => {
        const nicheToSearch = overrideNiche !== undefined ? overrideNiche : niche;
        const competitorToSearch = overrideCompetitor !== undefined ? overrideCompetitor : competitorSite;
        const locationToSearch = overrideLocation || locationCode;

        if (!nicheToSearch.trim()) {
            setError('Please enter a topic or niche to analyze');
            return;
        }

        // Reject if user accidentally entered a URL in the niche field
        if (!fromRecentSearch && /^https?:\/\//i.test(nicheToSearch.trim())) {
            setError('Please enter a keyword or niche (e.g., "AI tools"), not a URL, in the first field.');
            return;
        }

        if (!fromRecentSearch && competitorToSearch && competitorToSearch.trim()) {
            try {
                new URL(competitorToSearch.trim().startsWith('http') ? competitorToSearch.trim() : 'https://' + competitorToSearch.trim());
            } catch {
                setError('Please enter a valid competitor URL (e.g., https://competitor.com)');
                return;
            }
        }

        setLoading(true);
        setError(null);
        setAnalysisData(null);

        try {
            const response = await trendsAPI.findContentGaps(
                nicheToSearch.trim(),
                competitorToSearch?.trim() || null,
                locationToSearch,
                forceRefresh,
                fromRecentSearch
            );

            if (response.data.success) {
                const data = response.data.data;
                setAnalysisData(data);

                if (fromRecentSearch) {
                    setNiche(nicheToSearch);
                    setCompetitorSite(competitorToSearch || '');
                    setLocationCode(locationToSearch);
                }

                fetchRecentSearches();
            } else {
                setError(response.data.message || 'Analysis failed. Please try a different niche.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to run analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRecentPill = (recent) => {
        // Guard: if a stale entry has a URL stored as niche, show a clear error instead of failing silently
        if (/^https?:\/\//i.test(recent.niche)) {
            setError('This saved search is invalid (a URL was stored as the niche). Please run a new search with a keyword instead.');
            return;
        }
        handleAnalyze(false, recent.niche, recent.competitor_url || null, recent.location_code, true);
    };

    const handleGenerateArticle = (topic) => {
        setPrefilledTopic(topic);
        setShowQuickPost(true);
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'Easy': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
            case 'Medium': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
            case 'Hard': return 'bg-red-500/20 text-red-400 border-red-500/30';
            default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
        }
    };

    const formatCacheAge = (cachedAt) => {
        if (!cachedAt) return null;
        const diff = Math.floor((Date.now() - new Date(cachedAt)) / (1000 * 60 * 60 * 24));
        if (diff === 0) return 'today';
        if (diff === 1) return '1 day ago';
        return `${diff} days ago`;
    };

    const isNewsQuery = (topic) => {
        const t = topic.toLowerCase();
        return /\b(news today|today news|latest news|breaking news|news \d{4})\b/.test(t);
    };

    const stripDomain = (url) => {
        if (!url) return '';
        try {
            return new URL(url.startsWith('http') ? url : 'https://' + url).hostname.replace(/^www\./, '');
        } catch {
            return url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
        }
    };

    const gaps = analysisData?.gaps || [];
    const rawOpportunities = analysisData?.competitor_analyzed
        ? gaps.filter(g => g.is_gap)
        : gaps;
    const opportunities = kdFilter === 'all'
        ? rawOpportunities
        : rawOpportunities.filter(g => (g.difficulty || 'N/A').toLowerCase() === kdFilter);
    const coveredByCompetitor = analysisData?.competitor_analyzed
        ? gaps.filter(g => !g.is_gap)
        : [];

    return (
        <div className="text-zinc-50">
                <main className="flex-1 min-h-0 p-6 overflow-y-auto custom-scrollbar">
                    <div className="max-w-4xl mx-auto space-y-6">

                        {/* Header */}
                        <div className="text-center space-y-2 py-4">
                            <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                                🎯 Competitor Gap Analysis
                            </h1>
                            <p className="text-zinc-400 text-sm">
                                Enter your niche and a competitor's site to discover content gaps they haven't covered
                            </p>
                        </div>

                        {/* Input Section */}
                        <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                            <CardContent className="pt-6 space-y-4">

                                {/* Niche field with helper */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-zinc-300">Your Niche / Topic</label>
                                    <input
                                        type="text"
                                        className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                                        placeholder="e.g., AI tools, health and fitness, personal finance"
                                        value={niche}
                                        onChange={(e) => setNiche(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAnalyze(false)}
                                    />
                                    <p className="text-xs text-zinc-600">Use a specific sub-topic for best results — e.g. <span className="text-zinc-500">"intermittent fasting"</span> not <span className="text-zinc-500">"Health and Fitness"</span></p>
                                </div>

                                {/* Competitor URL field */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-zinc-300">
                                        Competitor Website <span className="text-zinc-600 font-normal">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                                        placeholder="e.g., https://openai.com"
                                        value={competitorSite}
                                        onChange={(e) => setCompetitorSite(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAnalyze(false)}
                                    />
                                    <p className="text-xs text-zinc-600">
                                        If provided, we'll scan their sitemap to find topics they haven't written about
                                    </p>
                                </div>

                                {/* Location */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-zinc-300">Location</label>
                                    <Select value={locationCode} onValueChange={setLocationCode}>
                                        <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                                            {TREND_LOCATIONS.map(loc => (
                                                <SelectItem key={loc.code} value={loc.code} className="text-zinc-300 focus:bg-zinc-800 focus:text-white">
                                                    {loc.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Button
                                    onClick={() => handleAnalyze(false)}
                                    disabled={loading}
                                    className="w-full bg-violet-600 hover:bg-violet-700 gap-2 text-white"
                                >
                                    {loading ? (
                                        <>
                                            <MagnifyingGlassIcon className="w-4 h-4 animate-pulse" />
                                            Analyzing...
                                        </>
                                    ) : (
                                        <>
                                            <MagnifyingGlassIcon className="w-4 h-4" />
                                            Find Content Gaps
                                        </>
                                    )}
                                </Button>

                                {error && (
                                    <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
                                        <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
                                        {error}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Recent Searches */}
                        {recentSearches.length > 0 && !analysisData && !loading && (
                            <div className="space-y-2">
                                <p className="text-xs text-zinc-500 font-medium flex items-center gap-1.5">
                                    <ClockIcon className="w-3 h-3" /> Recent searches
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {recentSearches.map((recent, idx) => (
                                        <Badge
                                            key={idx}
                                            onClick={() => handleRecentPill(recent)}
                                            className="cursor-pointer bg-zinc-800/60 hover:bg-zinc-700/80 text-zinc-300 border border-zinc-700/50 hover:border-zinc-500/50 transition-all font-medium px-3 py-1 max-w-xs truncate"
                                        >
                                            {recent.niche}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <div className="py-16 flex flex-col items-center justify-center space-y-4">
                                <div className="relative">
                                    <div className="w-14 h-14 border-4 border-white/5 rounded-full"></div>
                                    <div className="absolute top-0 left-0 w-14 h-14 border-4 border-t-violet-500 rounded-full animate-spin"></div>
                                </div>
                                <div className="text-center space-y-1">
                                    <p className="text-white font-medium">Scanning for content gaps...</p>
                                    <p className="text-zinc-500 text-sm">This may take 10–20 seconds on first run</p>
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {!analysisData && !loading && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                                {[
                                    {
                                        icon: <TargetIcon className="w-5 h-5 text-violet-400" />,
                                        color: 'from-violet-500/20 to-purple-500/20',
                                        title: 'Find Content Gaps',
                                        desc: 'Discover trending topics in your niche that your competitor hasn\'t written about yet.'
                                    },
                                    {
                                        icon: <BarChartIcon className="w-5 h-5 text-blue-400" />,
                                        color: 'from-blue-500/20 to-cyan-500/20',
                                        title: 'Real Keyword Data',
                                        desc: 'Every opportunity is backed by Google Trends growth data and DataForSEO difficulty scores.'
                                    },
                                    {
                                        icon: <RocketIcon className="w-5 h-5 text-emerald-400" />,
                                        color: 'from-emerald-500/20 to-green-500/20',
                                        title: 'One-Click Articles',
                                        desc: 'Spot an opportunity? Generate a full article with one click and add it to your queue.'
                                    }
                                ].map((item, i) => (
                                    <div key={i} className="bg-zinc-900/40 border border-white/10 rounded-xl p-4">
                                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-3`}>
                                            {item.icon}
                                        </div>
                                        <h3 className="text-sm font-semibold text-white mb-1">{item.title}</h3>
                                        <p className="text-xs text-zinc-400 leading-relaxed">{item.desc}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Results */}
                        {analysisData && !loading && (
                            <div className="space-y-5">

                                {/* Summary banner */}
                                <div className="p-4 bg-zinc-900/60 border border-white/10 rounded-xl space-y-3">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="space-y-1">
                                            <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Analysis for</p>
                                            <p className="text-white font-semibold text-base">{analysisData.niche}</p>
                                            {analysisData.competitor_analyzed && analysisData.competitor_url && (
                                                <p className="text-sm text-zinc-400">
                                                    Compared against <span className="text-violet-400">{stripDomain(analysisData.competitor_url)}</span>
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {analysisData.is_fresh ? (
                                                <span className="flex items-center gap-1.5 text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                                    Live data
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1.5 text-zinc-400 text-xs bg-zinc-800/60 border border-zinc-700/50 rounded-full px-2.5 py-1">
                                                    <ClockIcon className="w-3 h-3" />
                                                    Cached · {formatCacheAge(analysisData.cached_at)}
                                                </span>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-zinc-400 hover:text-white gap-1.5 h-7 text-xs border border-zinc-700/50 hover:border-zinc-500"
                                                onClick={() => handleAnalyze(true)}
                                            >
                                                <ReloadIcon className="w-3 h-3" />
                                                Refresh
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Stats row */}
                                    <div className="grid grid-cols-3 gap-3 pt-2 border-t border-white/5">
                                        <div>
                                            <p className="text-xs text-zinc-500">Opportunities found</p>
                                            <p className="text-xl font-bold text-emerald-400">{opportunities.length}</p>
                                        </div>
                                        {analysisData.competitor_analyzed && (
                                            <>
                                                <div>
                                                    <p className="text-xs text-zinc-500">Competitor covers</p>
                                                    <p className="text-xl font-bold text-zinc-300">{coveredByCompetitor.length}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-zinc-500">Total topics analyzed</p>
                                                    <p className="text-xl font-bold text-zinc-300">{gaps.length}</p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Competitor warning */}
                                {analysisData.competitor_warning === 'competitor_blocked' && (
                                    <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg text-orange-300 text-sm">
                                        <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="font-medium mb-0.5">Competitor sitemap unavailable</p>
                                            <p className="text-orange-400/70 text-xs">
                                                Couldn't scan {stripDomain(analysisData.competitor_url || '')} — their site may block automated access.
                                                Showing all trending topics for your niche instead.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* What this means — context box */}
                                {analysisData.competitor_analyzed && !analysisData.competitor_warning && (
                                    <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-300 text-sm">
                                        <InfoCircledIcon className="w-4 h-4 mt-0.5 shrink-0" />
                                        <p>
                                            We found <strong>{opportunities.length} trending topics</strong> in "{analysisData.niche}" that{' '}
                                            <strong>{stripDomain(analysisData.competitor_url || '')}</strong> hasn't published about.
                                            These are your content opportunities.
                                        </p>
                                    </div>
                                )}

                                {/* Competitor's covered topics — collapsed by default */}
                                {analysisData.competitor_analyzed && coveredByCompetitor.length > 0 && (
                                    <details className="group">
                                        <summary className="cursor-pointer flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 transition-colors list-none">
                                            <CheckCircledIcon className="w-4 h-4 text-zinc-600" />
                                            <span>
                                                {coveredByCompetitor.length} topics already covered by {stripDomain(analysisData.competitor_url || 'competitor')}
                                            </span>
                                            <span className="text-zinc-600 text-xs group-open:hidden">(click to expand)</span>
                                        </summary>
                                        <div className="mt-3 flex flex-wrap gap-2 pl-6">
                                            {coveredByCompetitor.slice(0, 20).map((gap, idx) => (
                                                <span key={idx} className="text-xs bg-zinc-800/60 text-zinc-500 border border-zinc-700/40 rounded-full px-3 py-1">
                                                    {gap.topic}
                                                </span>
                                            ))}
                                            {coveredByCompetitor.length > 20 && (
                                                <span className="text-xs text-zinc-600 px-3 py-1">+{coveredByCompetitor.length - 20} more</span>
                                            )}
                                        </div>
                                    </details>
                                )}

                                {/* Opportunities list */}
                                {opportunities.length === 0 ? (
                                    <div className="py-10 text-center text-zinc-500 text-sm">
                                        No significant gaps found. Try a broader niche or a different competitor.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <h2 className="text-lg font-bold text-white">Content Opportunities</h2>
                                            <div className="flex items-center gap-1.5">
                                                {['all', 'easy', 'medium', 'hard'].map(f => (
                                                    <button key={f} onClick={() => setKdFilter(f)}
                                                        className={`text-xs px-2.5 py-1 rounded-full border transition-all capitalize ${
                                                            kdFilter === f
                                                                ? 'bg-violet-600 border-violet-500 text-white'
                                                                : 'bg-zinc-800/60 border-zinc-700/50 text-zinc-400 hover:text-zinc-200'
                                                        }`}>
                                                        {f === 'all' ? `All (${rawOpportunities.length})` : f}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        {opportunities.some(g => isNewsQuery(g.topic)) && (
                                            <p className="text-xs text-zinc-500">
                                                Topics marked <span className="text-zinc-400">News query</span> are trending search spikes — they're real gaps but may not suit evergreen articles.
                                            </p>
                                        )}

                                        <div className="space-y-3">
                                            {opportunities.map((gap, idx) => {
                                                const newsQuery = isNewsQuery(gap.topic);
                                                return (
                                                <div
                                                    key={gap.id || idx}
                                                    className={`flex items-start gap-4 p-4 border rounded-xl transition-all ${newsQuery ? 'bg-zinc-900/20 border-zinc-700/30 opacity-70' : 'bg-zinc-900/40 border-white/10 hover:border-white/20'}`}
                                                >
                                                    {/* Rank */}
                                                    <span className="text-lg font-bold text-zinc-600 w-6 shrink-0 pt-0.5">{idx + 1}</span>

                                                    {/* Main content */}
                                                    <div className="flex-1 min-w-0 space-y-2">
                                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                                            <p className={`font-medium text-sm leading-snug ${newsQuery ? 'text-zinc-400' : 'text-white'}`}>{gap.topic}</p>
                                                            <div className="flex items-center gap-1.5 shrink-0">
                                                                {newsQuery && (
                                                                    <Badge className="bg-zinc-700/40 hover:bg-zinc-700/40 text-zinc-500 border-zinc-600/30 text-xs">
                                                                        News query
                                                                    </Badge>
                                                                )}
                                                                {gap.breakout && !newsQuery && (
                                                                    <Badge className="bg-orange-500/20 hover:bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                                                                        🔥 Breakout
                                                                    </Badge>
                                                                )}
                                                                <Badge className={`hover:bg-transparent text-xs ${getDifficultyColor(gap.difficulty)}`}>
                                                                    {gap.difficulty}
                                                                </Badge>
                                                            </div>
                                                        </div>

                                                        {/* Stats row */}
                                                        <div className="flex items-center gap-4 flex-wrap">
                                                            <span className="flex items-center gap-1 text-xs text-zinc-400">
                                                                <LightningBoltIcon className="w-3 h-3 text-orange-400" />
                                                                <span className="text-orange-400 font-medium">{gap.trend}</span>
                                                                <span className="text-zinc-600">growth</span>
                                                            </span>
                                                            <span className="flex items-center gap-1 text-xs text-zinc-400">
                                                                <BarChartIcon className="w-3 h-3 text-blue-400" />
                                                                <span className="text-blue-400 font-medium">{gap.search_volume}</span>
                                                                <span className="text-zinc-600">/mo</span>
                                                            </span>
                                                            <span className="flex items-center gap-1 text-xs text-zinc-400">
                                                                <ArrowUpIcon className="w-3 h-3 text-violet-400" />
                                                                <span className="text-violet-400 font-medium">{gap.opportunity_score}</span>
                                                                <span className="text-zinc-600">score</span>
                                                            </span>
                                                            {gap.keyword_difficulty !== null && gap.keyword_difficulty !== undefined && (
                                                                <span className="text-xs text-zinc-500">
                                                                    KD: <span className="text-zinc-300">{gap.keyword_difficulty}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Action */}
                                                    <Button
                                                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 shrink-0 h-6 px-2.5 text-xs font-semibold rounded-md"
                                                        onClick={() => handleGenerateArticle(gap.topic)}
                                                    >
                                                        <PlusIcon className="w-3 h-3" />
                                                        Write
                                                    </Button>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Bottom actions */}
                                {opportunities.length > 0 && (
                                    <div className="flex gap-3 pt-2">
                                        <Button
                                            className="flex-1 bg-violet-600 hover:bg-violet-700 gap-2 text-white"
                                            onClick={() => handleGenerateArticle(opportunities[0]?.topic || '')}
                                        >
                                            <PlusIcon className="w-4 h-4" />
                                            Write Top Opportunity
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="gap-2"
                                            onClick={() => { setAnalysisData(null); setNiche(''); setCompetitorSite(''); }}
                                        >
                                            <MagnifyingGlassIcon className="w-4 h-4" />
                                            New Search
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                </main>

            {showQuickPost && (
                <QuickPost
                    user={user}
                    prefilledTopic={prefilledTopic}
                    onClose={(reason) => {
                        setShowQuickPost(false);
                        if (reason === 'topics') navigate('/topic-queue');
                    }}
                />
            )}
        </div>
    );
}

export default CompetitorAnalysis;
